import reactGA from "react-ga4";
import { GA4_ENV, VALID_ROUTES } from "../constants/analytics";
import { ALLOW_PERFORMANCE_COOKIES } from "../constants/storage";
import {
  GA4EventName,
  GA4EventParams,
  GA4UserPropertyKey,
} from "../types/analytics";
import { IS_LOCALHOST } from "../constants/environment";

/**
 * GoogleAnalyticsV2 - Refactored GA4 analytics utility
 *
 * This class provides proper GA4 event tracking with structured parameters,
 * replacing the legacy Universal Analytics model (category + action) with
 * the GA4 model (event_name + parameters).
 *
 * Key improvements over GoogleAnalytics (V1):
 * - Events use descriptive names instead of category + action
 * - All contextual data is sent as structured parameters (not concatenated strings)
 * - User properties are set for audience segmentation
 * - Web vitals include metric name, value, and rating as parameters
 * - Page load timing includes load_time_ms and render_time_ms as parameters
 *
 */
export default class GoogleAnalyticsV2 {
  public static isInitialized: boolean = false;

  /** Make the constructor private to block instantiation outside of the class. */
  private constructor() {}

  /**
   * Initialize GA4 tracking with environment-specific measurement ID.
   * Also sets default user properties if available.
   *
   * @param host - The current hostname used to look up the GA4 measurement ID
   */
  public static initialize(host: string): void {
    const cookieExpiry = 31536000; // override default 2 yrs cookie expiry to just 1 yr
    const isGoogleCookiesAllowed =
      localStorage.getItem(ALLOW_PERFORMANCE_COOKIES) === "true";

    if (!isGoogleCookiesAllowed || this.isInitialized) return;

    reactGA.initialize(GA4_ENV[host], {
      gaOptions: {
        cookieExpires: cookieExpiry,
      },
      gtagOptions: {
        send_page_view: false,
      },
    });

    this.isInitialized = true;
  }

  /**
   * Send a page view event to GA4.
   * Should only be called from AnalyticsTracker to avoid duplicate page views.
   *
   * @param page - The page path (e.g., "/my-data")
   * @param title - Optional page title
   */
  public static sendPageView(page: string, title?: string): void {
    if (!this.isInitialized) return;

    // Catch successful initial login
    if (window.location.search === "?success=1") {
      reactGA.send({ hitType: "pageview", page: "/login-success" });
    }

    if (VALID_ROUTES.includes(page)) {
      reactGA.send({ hitType: "pageview", page, title: title || page });
    }
  }

  /**
   * Track a GA4 event with structured parameters.
   *
   * This is the core event tracking method. Unlike the old triggerEvent()
   * which only accepted category + action, this method accepts any GA4 event
   * name and a parameters object with structured data.
   *
   * @param eventName - The GA4 event name (use GA4EventName enum)
   * @param params - Event parameters as key-value pairs
   *
   * @example
   * GoogleAnalyticsV2.trackEvent(GA4EventName.HeaderClick, {
   *   action: "my-data",
   *   country_code: "US",
   *   source: "gnb",
   * });
   */
  public static trackEvent(
    eventName: GA4EventName | string,
    params: GA4EventParams = {}
  ): void {
    if (!this.isInitialized) return;
    // Debug logging for local development
    if (IS_LOCALHOST) {
      console.log(`[GA4] ${eventName}`, params);
    }
    reactGA.event({
      category: eventName,
      action: eventName,
      ...params,
    });
  }

  /**
   * Track a page load performance event with timing data.
   *
   * @param path - The page path that was loaded
   * @param loadTime - Page load time in milliseconds
   * @param renderTime - Optional render time in milliseconds
   */
  public static trackPageLoad(
    path: string,
    loadTime: number,
    renderTime?: number
  ): void {
    if (!this.isInitialized) return;

    this.trackEvent(GA4EventName.PageLoad, {
      page_path: path,
      load_time_ms: Math.round(loadTime),
      render_time_ms: renderTime ? Math.round(renderTime) : undefined,
    });
  }

  /**
   * Track a web vital metric event.
   *
   * @param metricName - The web vital metric name (e.g., "CLS", "FID", "LCP")
   * @param value - The metric value
   * @param rating - The metric rating ("good", "needs-improvement", "poor")
   */
  public static trackWebVital(
    metricName: string,
    value: number,
    rating: string
  ): void {
    if (!this.isInitialized) return;

    this.trackEvent(GA4EventName.WebVital, {
      metric_name: metricName,
      metric_value: Math.round(value),
      metric_rating: rating,
    });
  }

  /**
   * Set a user property in GA4 for audience segmentation.
   *
   * @param key - The user property key (use GA4UserPropertyKey enum)
   * @param value - The user property value
   *
   * @example
   * GoogleAnalyticsV2.setUserProperty(GA4UserPropertyKey.CountryCode, "US");
   */
  public static setUserProperty(
    key: GA4UserPropertyKey | string,
    value: string | number | boolean
  ): void {
    if (!this.isInitialized) return;

    reactGA.set({ [key]: value });
  }

  /**
   * Set multiple user properties at once.
   *
   * @param properties - Object with user property key-value pairs
   *
   * @example
   * GoogleAnalyticsV2.setUserProperties({
   *   country_code: "US",
   *   has_local_site: true,
   *   login_type: "sa",
   * });
   */
  public static setUserProperties(
    properties: Record<string, string | number | boolean>
  ): void {
    if (!this.isInitialized) return;

    reactGA.set(properties);
  }

  /**
   * Disable GA4 tracking and clean up global references.
   */
  public static disableTracking(): void {
    delete window.GoogleAnalyticsObject;
    delete window.ga;
    delete window.gaData;
    delete window.gaGlobal;
    delete window.gaplugins;
    delete window.google_tag_data;

    this.isInitialized = false;
  }
}
